import React from 'react';
import { useAccountIntelSummary } from '../contact/hooks';
import { GenerateIntelligenceButton } from './GenerateIntelligenceButton';
import { renderBoldText } from '../renderBoldText';

/**
 * Account Intelligence panel "Summary" tab (plan
 * `plans/2026-09-23-002-rep-prompt-overrides-and-account-intelligence-summary-plan.md`
 * U8, R16) — the Terra-side AI digest of the local `account_intelligence`
 * mirror row, streamed via `useAccountIntelSummary`. Rendering mirrors
 * `ContactIntelPanel.tsx` (shared `renderBoldText`, streaming caret,
 * Regenerate), with one extra state: `isEmpty`, which this panel's route
 * uses for "the account has a mirror row but nothing to summarize" instead
 * of surfacing that as an error.
 */
export function AccountIntelSummaryView({ accountId }: { accountId: string | null }) {
  const { text, isStreaming, error, isEmpty, regenerate } = useAccountIntelSummary(accountId, !!accountId);
  const muted = 'text-sm text-content-tertiary dark:text-nightshade-500';

  if (!accountId) return <div className={muted}>No account linked.</div>;

  if (error) {
    return (
      <div>
        <p className="text-sm text-content-danger dark:text-red-400">Intelligence unavailable.</p>
        <button
          type="button"
          onClick={regenerate}
          className="mt-1.5 text-[11px] font-medium text-content-action dark:text-blueberry-400 hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (isEmpty && !isStreaming) {
    return (
      <div>
        <p className={muted}>No intelligence for this account yet.</p>
        <GenerateIntelligenceButton accountId={accountId} className="mt-2" />
      </div>
    );
  }

  return (
    <div>
      <div className="whitespace-pre-wrap text-sm text-content-primary dark:text-nightshade-300">
        {renderBoldText(text)}
        {isStreaming && <span className="animate-pulse" aria-hidden="true">▍</span>}
      </div>
      {!isStreaming && text && (
        <button
          type="button"
          onClick={regenerate}
          className="mt-2 text-[11px] font-medium text-content-action dark:text-blueberry-400 hover:underline"
        >
          Regenerate
        </button>
      )}
    </div>
  );
}
